import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import api from '@/services/api'
import { opportunitiesService } from '@/services/opportunities-service'

interface Estimator {
  id: string
  displayName: string
  email: string
}

interface Props {
  opportunityId: string
  opportunityTitle: string
  opportunityAddress?: string
  opportunityProjectTypes?: string
  opportunityScope?: string
  opportunityContact?: string
  opportunityContactEmail?: string
  onClose: () => void
  onConverted: (rfpId: string) => void
}

export function ConvertToRfpModal({
  opportunityId,
  opportunityTitle,
  opportunityAddress,
  opportunityProjectTypes,
  opportunityScope,
  opportunityContact,
  opportunityContactEmail,
  onClose,
  onConverted,
}: Props) {
  const [loading, setLoading] = useState(false)
  const [loadingEstimators, setLoadingEstimators] = useState(true)
  const [estimators, setEstimators] = useState<Estimator[]>([])
  const [selectedEstimatorId, setSelectedEstimatorId] = useState('')

  useEffect(() => {
    const fetchEstimators = async () => {
      try {
        setLoadingEstimators(true)
        const response = await api.get('/users?role=estimator')
        setEstimators(response.data?.data || [])
        if (response.data?.data?.length > 0) {
          setSelectedEstimatorId(response.data.data[0].id)
        }
      } catch (err) {
        console.error('Failed to fetch estimators:', err)
      } finally {
        setLoadingEstimators(false)
      }
    }

    fetchEstimators()
  }, [])

  const handleConvert = async () => {
    if (!selectedEstimatorId) return

    try {
      setLoading(true)
      const result = await opportunitiesService.convertToRfp(opportunityId, selectedEstimatorId)
      onConverted(result.id || result.rfpId)
    } catch (err) {
      console.error('Failed to convert to RFP:', err)
    } finally {
      setLoading(false)
    }
  }

  const projectTypesList: string[] = (() => {
    try {
      return JSON.parse(opportunityProjectTypes || '[]')
    } catch {
      return []
    }
  })()

  const scopePreview = opportunityScope ? opportunityScope.substring(0, 200) + (opportunityScope.length > 200 ? '...' : '') : ''

  const modal = (
    <div className="opp-convert-overlay">
      <div className="opp-convert-dialog">
        <h2 className="opp-convert-title">Convert to RFP</h2>

        {/* Title */}
        <div className="opp-convert-field">
          <label>Title</label>
          <div className="value">{opportunityTitle}</div>
        </div>

        {/* Address */}
        {opportunityAddress && (
          <div className="opp-convert-field">
            <label>Project Address</label>
            <div className="value">{opportunityAddress}</div>
          </div>
        )}

        {/* Project Types */}
        {projectTypesList.length > 0 && (
          <div className="opp-convert-field">
            <label>Project Types</label>
            <div className="opp-convert-pills">
              {projectTypesList.map((type: string) => (
                <span key={type} className="opp-convert-pill">{type}</span>
              ))}
            </div>
          </div>
        )}

        {/* Scope Preview */}
        {scopePreview && (
          <div className="opp-convert-field">
            <label>Scope</label>
            <div className="value" style={{ fontSize: '13px', color: 'var(--s600)' }}>{scopePreview}</div>
          </div>
        )}

        {/* Contact */}
        {(opportunityContact || opportunityContactEmail) && (
          <div className="opp-convert-field">
            <label>Contact</label>
            <div className="value">
              {opportunityContact && <div>{opportunityContact}</div>}
              {opportunityContactEmail && <div>{opportunityContactEmail}</div>}
            </div>
          </div>
        )}

        {/* Estimator Selection */}
        <div className="opp-convert-field">
          <label>Assign to Estimator *</label>
          {loadingEstimators ? (
            <div style={{ padding: '0.75rem', color: 'var(--s600)', fontSize: '0.875rem' }}>
              Loading estimators...
            </div>
          ) : (
            <select
              className="opp-form-select"
              value={selectedEstimatorId}
              onChange={e => setSelectedEstimatorId(e.target.value)}
              disabled={loading || estimators.length === 0}
            >
              <option value="">Select an estimator</option>
              {estimators.map(est => (
                <option key={est.id} value={est.id}>
                  {est.displayName} ({est.email})
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Actions */}
        <div className="opp-convert-actions">
          <button
            className="opp-convert-btn secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            className="opp-convert-btn primary"
            onClick={handleConvert}
            disabled={!selectedEstimatorId || loading || loadingEstimators}
          >
            {loading ? 'Converting...' : 'Convert to RFP'}
          </button>
        </div>
      </div>
    </div>
  )

  return createPortal(modal, document.body)
}
